import { useCallback, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { dashboardApi } from 'app/services/dashboardApi';
import { useCurrentUser, getBootUserId } from 'hooks/useDashboardData';
import { getCookie, getKsdmConfig } from 'helpers/index';
import type { IDashboardUser } from 'models';

const { useUpdateProfileMutation, useGetDepartmentsQuery } = dashboardApi;

/**
 * Edit-profile popup (QA #8) — faithful port of the legacy user-menu popup
 * (`/static/mvc/edit_profile/tmpl/edit_profile.tmpl` + `view/edit_profile.js`),
 * opened from the header's `#edit_profile_button`. Markup keeps the legacy
 * class names so the `.edit-profile-popup` styles already in common.css
 * (loaded globally by react.html; source `sass/ui/popup/_user.profile.scss`)
 * apply unchanged.
 *
 * Layout: left avatar column (image, Remove/Upload, "JPEG and PNG files only",
 * name/company/department), "Personal Information" (First*, Middle, Last*,
 * Initials* 3 chars, Company/Department/Username read-only, Password* with
 * Show/Hide + conditions tooltip), "Contact Information" (Work + Ext., Mobile,
 * Email, read-only Access Type / User Skill / Role), footnotes, Cancel / Done.
 *
 * Behavior (edit_profile.js):
 *  - Only CHANGED inputs are collected ('' → null) and PATCHed on Done
 *    (:95-108, user model urlRoot `/api/v1.0/users/` → PATCH /users/{id}).
 *    Success: `redirect` true (own password changed) → location.reload();
 *    else close (the header initials/avatar follow the refreshed User cache —
 *    legacy patches `#user-initials` directly). Error: `.req-error` shows
 *    `error` or `username[0]` from the response (:103-105), or a generic
 *    message when the response carries neither, so a popup that stays open
 *    always says why. Nothing collected → close without a request; an emptied
 *    password is dropped rather than sent as null (see onFieldChange/onDone).
 *  - Cancel is a pure close (:112-114). No outside-click close; Esc works via
 *    the global popup handler (usePopupKeys clicks button[name="cancel"], the
 *    same MainView body-keydown behavior the legacy popup gets for free).
 *  - Validation: native constraints (First/Last 3-30, Initials exactly 3,
 *    Password 7-55 + charset regex on keyup(100ms) → `.invalid`, phone
 *    `pattern`s, email type) — any `.invalid, input:invalid` in the popup adds
 *    `.non-clickable` to Done (checkButtonClickability :212-218; common.css
 *    `.non-clickable{pointer-events:none;opacity:.5}` blocks the click).
 *  - changeInput (:165-179): live name heading, empty password resets the
 *    Show/Hide toggle, clears `.req-error`, re-checks Done.
 *  - Avatar: file-input change → raw multipart PATCH of
 *    FormData(#avatar_upload_form) to /api/v1.0/users/{id} with X-CSRFToken
 *    (:116-134); on 200 the returned `avatar` URL updates the popup image and
 *    the header (via the getCurrentUser cache). Remove → PATCH {avatar:null}
 *    + clear the file input (:155-163).
 *  - Phone inputs filter keypresses to `0123456789-+(). ` (uni_tools
 *    mobileKeyPress); email filters keypresses to `[a-zA-Z0-9@._-]` and blocks
 *    non-English pastes (accesptOnlyEnglish / blockPaste).
 *
 * Deviations (deliberate): the legacy events map binds 'submit form' to a
 * handler that doesn't exist (Backbone silently skips it), so Enter in the
 * password field would NATIVELY submit the little autocomplete-off form and
 * reload the page — here the forms preventDefault instead.
 */

/** edit_profile.js:201 keyupPassword charset/length rule. */
const PASSWORD_REGEX = /^[A-Za-z0-9!@#$%^&*()\-_=+[\]{}|;:'",.<>?/\\]{7,55}$/;

/** uni_tools.mobileKeyPress allowed characters. */
const PHONE_ALLOWED_CHARS = '0123456789-+(). ';

/** uni_tools.accesptOnlyEnglish per-character rule (email keypress). */
const EMAIL_CHAR_REGEX = /[a-zA-Z0-9@._-]/;

/** blockPaste English-only rule (email paste). */
const ENGLISH_ONLY_REGEX = /^[A-Za-z0-9\s.,!?@#$%^&*()_\-+=:;"'<>/\\[\]{}|`~]*$/;

interface EditProfilePopupProps {
  onClose: () => void;
}

const EditProfilePopup = ({ onClose }: EditProfilePopupProps) => {
  const dispatch = useAppDispatch();
  const userId = getBootUserId();
  const { data: user } = useCurrentUser();
  const { data: departments } = useGetDepartmentsQuery();
  const [updateProfile] = useUpdateProfileMutation();

  const mfg = getKsdmConfig()?.mfg ?? null;
  const mfgName = mfg?.name ?? '';

  const rootRef = useRef<HTMLDivElement | null>(null);
  const avatarFormRef = useRef<HTMLFormElement | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const firstNameRef = useRef<HTMLInputElement | null>(null);
  const middleNameRef = useRef<HTMLInputElement | null>(null);
  const lastNameRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const pwdTimerRef = useRef<number | null>(null);

  // Changed inputs only, '' recorded as null (edit_profile.js render():83-91).
  const valuesRef = useRef<Partial<IDashboardUser>>({});

  const [fullName, setFullName] = useState<string | null>(null);
  const [reqError, setReqError] = useState('');
  const [doneBlocked, setDoneBlocked] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);

  // checkButtonClickability (:212-218): any `.invalid, input:invalid` inside
  // the popup blocks Done with `.non-clickable`.
  const checkButtonClickability = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    setDoneBlocked(root.querySelectorAll('.invalid, input:invalid').length > 0);
  }, []);

  // changeInput (:165-179) + the render()-bound values recorder (:83-91).
  const onFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      // An emptied password means "leave my password alone", never "set it to
      // nothing" — drop the key rather than recording the usual '' → null.
      // `partial_update` branches on `'password' in request.data` and then does
      // `len(request.data['password'])` (rest_user/viewset.py:133), so a null
      // password is a TypeError → 500 → Done appears to do nothing. Every other
      // field keeps the legacy null (edit_profile.js:83-91).
      if (name === 'password' && value === '') delete valuesRef.current.password;
      else valuesRef.current[name] = value === '' ? null : value;
      setFullName(
        `${firstNameRef.current?.value ?? ''} ${middleNameRef.current?.value ?? ''} ${
          lastNameRef.current?.value ?? ''
        }`
      );
      // Empty password resets the Show/Hide toggle and re-masks the field.
      if (!passwordRef.current?.value) setPwdVisible(false);
      setReqError('');
      checkButtonClickability();
    },
    [checkButtonClickability]
  );

  // keyupPassword (:199-210): 100ms after a keystroke, flag a non-empty value
  // failing the charset/length regex with `.invalid` (class managed directly on
  // the uncontrolled input, as legacy does), then re-check Done.
  const onPasswordKeyUp = useCallback(() => {
    if (pwdTimerRef.current != null) window.clearTimeout(pwdTimerRef.current);
    pwdTimerRef.current = window.setTimeout(() => {
      const input = passwordRef.current;
      if (!input) return;
      if (input.value && !PASSWORD_REGEX.test(input.value)) input.classList.add('invalid');
      else input.classList.remove('invalid');
      checkButtonClickability();
    }, 100);
  }, [checkButtonClickability]);

  // showPassword / hidePassword (:181-198) — only toggle when non-empty.
  const showPassword = useCallback(() => {
    if (passwordRef.current?.value) setPwdVisible(true);
  }, []);
  const hidePassword = useCallback(() => {
    if (passwordRef.current?.value) setPwdVisible(false);
  }, []);

  // uni_tools.mobileKeyPress — keypress only fires for character keys, so
  // control/navigation keys and shortcuts pass through like legacy.
  const onPhoneKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!PHONE_ALLOWED_CHARS.includes(e.key)) e.preventDefault();
  }, []);

  // uni_tools.accesptOnlyEnglish (email keypress).
  const onEmailKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length === 1 && !EMAIL_CHAR_REGEX.test(e.key)) e.preventDefault();
  }, []);

  // blockPaste (email).
  const onEmailPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData?.getData('text');
    if (pasted != null && !ENGLISH_ONLY_REGEX.test(pasted)) e.preventDefault();
  }, []);

  // doneBtn (:95-110): PATCH the collected values, then close.
  const onDone = useCallback(async () => {
    if (userId == null) return;
    // Nothing was edited — close without a request. Legacy fires a `{}` PATCH
    // here (Backbone `save({}, {patch:true})`), which the server accepts as a
    // no-op, so skipping it saves a round trip and, more to the point, takes
    // the network out of the two paths where the user changed no form field:
    // Done straight after opening, and Done after only swapping the avatar
    // (which uploads itself and never touches `values`). Those are the cases
    // QA saw the popup refuse to close.
    if (Object.keys(valuesRef.current).length === 0) {
      onClose();
      return;
    }
    try {
      const res = await updateProfile({ id: userId, body: valuesRef.current }).unwrap();
      if (res.redirect) {
        // Own password changed → session re-auth needed (viewset partial_update).
        window.location.reload();
        return;
      }
      onClose();
    } catch (err) {
      const data = (err as { data?: { error?: string; username?: string[] } }).data;
      // A save that fails must say so: legacy renders `err.responseJSON.error`
      // straight into `.req-error`, which is blank whenever the response isn't
      // one of its two known shapes (a 500's HTML body, say) — leaving a popup
      // that won't close and won't explain why.
      setReqError(
        data?.error ?? data?.username?.[0] ?? 'Could not save your profile. Please try again.'
      );
    }
  }, [userId, updateProfile, onClose]);

  // upload_image (:116-134): raw multipart PATCH of the avatar form.
  const onAvatarChange = useCallback(() => {
    if (userId == null || !avatarFormRef.current) return;
    const body = new FormData(avatarFormRef.current);
    const token = getCookie('csrftoken');
    fetch(`/api/v1.0/users/${userId}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: token ? { 'X-CSRFToken': token } : undefined,
      body,
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data: { avatar?: string | null }) => {
        // updateAvatar (:136-153): the popup image and the header avatar both
        // render from the getCurrentUser cache — patch it in place.
        dispatch(
          dashboardApi.util.updateQueryData('getCurrentUser', userId, (draft) => {
            draft.avatar = data.avatar ?? null;
          })
        );
      })
      .catch(() => {
        /* legacy xhr.onload only handles 200 — errors are ignored */
      });
  }, [userId, dispatch]);

  // removeImg (:155-163): PATCH {avatar:null} + clear the file input; swap the
  // images back to the account icon immediately (legacy does it via the DOM).
  const onRemoveAvatar = useCallback(() => {
    if (userId == null) return;
    updateProfile({ id: userId, body: { avatar: null } });
    dispatch(
      dashboardApi.util.updateQueryData('getCurrentUser', userId, (draft) => {
        draft.avatar = null;
      })
    );
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  }, [userId, updateProfile, dispatch]);

  // dep_name (:57-65) — resolve department_id against the departments list.
  const depName = useMemo(() => {
    const depId = user?.department_id;
    if (!departments || depId == null) return '';
    return departments.find((d) => d.id === Number(depId))?.name ?? '';
  }, [departments, user?.department_id]);

  // access_type (:66-71).
  const accessType =
    user?.access_type_id === 1 ? 'Project' : user?.access_type_id === 2 ? 'Network' : '';

  // Legacy initialize() waits for the user fetch before rendering.
  if (!user || userId == null) return null;

  const displayName =
    fullName ??
    `${user.first_name ?? ''} ${user.middle_name ?? ''} ${user.last_name ?? ''}`;

  return (
    <div className="popup-wrapper edit-profile-popup active" ref={rootRef}>
      <div className="col-xs-12">
        <div className="popup-content list-inline row">
          <div id="avatar_upload" className="col-xs-3">
            <div className="profile-picture row">
              <div className="col-xs-12">
                <form
                  id="avatar_upload_form"
                  ref={avatarFormRef}
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="editing-btns row">
                    <button
                      type="button"
                      name="remove_btn"
                      className="editing-btn"
                      onClick={onRemoveAvatar}
                    >
                      Remove
                    </button>{' '}
                    {/* Same load-bearing word-space as the label/input pairs
                        below — edit_profile.tmpl:9-10 breaks the line between
                        the two `.editing-btn`s and nothing in CSS replaces the
                        gap. The pair is centred, so losing it pulled Remove 2px
                        right and Upload 2px left of legacy. */}
                    <label className="editing-btn">
                      <input
                        id="avatar"
                        ref={avatarInputRef}
                        type="file"
                        name="avatar"
                        className="hidden"
                        accept="image/png, image/jpeg"
                        onChange={onAvatarChange}
                      />
                      <span className="file-label">Upload</span>
                    </label>
                  </div>
                </form>
                <div className="row image-wrapper">
                  {user.avatar ? (
                    <>
                      <img id="profile_picture" src={user.avatar} alt="" />
                      <i />
                    </>
                  ) : (
                    <i className="icon-account" />
                  )}
                  <span className="img-format-hint">JPEG and PNG files only</span>
                </div>
                <div className="row">
                  <h4 className="first-middle-last-name">{displayName}</h4>
                  <h5 className="co-name">{mfgName}</h5>
                  <h5 className="dep-name">{depName}</h5>
                </div>
              </div>
            </div>
          </div>
          <div className="profile-info col-xs-9">
            <div className="list-inline row">
              <div className="col-xs-12">
                <div className="row">
                  <div className="personal-info col-xs-7">
                    <div className="col-xs-12">
                      <h4>Personal Information</h4>
                    </div>
                    {/* The `{' '}` between every `.label` span and its input is
                        load-bearing, here and in Contact Information. Both are
                        inline-block and `.label` is a fixed 130px
                        (_user.profile.scss:316-320) with no margin, so the
                        underline's left edge is `label + one word-space` —
                        edit_profile.tmpl:46 has a newline there and legacy
                        renders the collapsed space. JSX drops it, which pulled
                        every input and its underline 4px left of legacy (and
                        the password Show link 8px, it being one junction
                        further along). Measured dev vs dev3 on the same user:
                        inputs at x=789 vs 785, Ext. at 1357.8 vs 1349.8. */}
                    <div className="popup-inputs-group row">
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label required">First Name</span>{' '}
                            <input
                              ref={firstNameRef}
                              type="text"
                              name="first_name"
                              defaultValue={user.first_name ?? ''}
                              minLength={3}
                              maxLength={30}
                              onChange={onFieldChange}
                            />
                            <span className="note">3 - 30 characters</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label">Middle Name</span>{' '}
                            <input
                              ref={middleNameRef}
                              type="text"
                              name="middle_name"
                              defaultValue={user.middle_name ?? ''}
                              minLength={0}
                              maxLength={30}
                              onChange={onFieldChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label required">Last Name</span>{' '}
                            <input
                              ref={lastNameRef}
                              type="text"
                              name="last_name"
                              defaultValue={user.last_name ?? ''}
                              minLength={3}
                              maxLength={30}
                              onChange={onFieldChange}
                            />
                            <span className="note">3 - 30 characters</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label required">Initials</span>{' '}
                            <input
                              type="text"
                              name="initials"
                              defaultValue={user.initials ?? ''}
                              minLength={3}
                              maxLength={3}
                              onChange={onFieldChange}
                            />
                            <span className="note">3 characters</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label">Company</span>{' '}
                            <input type="text" name="company" value={mfgName} readOnly />
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label">Department</span>{' '}
                            <input type="text" name="dep" value={depName} readOnly />
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label required set-by-admin">Username</span>{' '}
                            <input
                              type="text"
                              name="username"
                              value={user.username ?? ''}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                            <div className="col-xs-12">
                              <span className="label required">Password</span>{' '}
                              <input
                                ref={passwordRef}
                                type={pwdVisible ? 'text' : 'password'}
                                name="password"
                                defaultValue=""
                                minLength={7}
                                maxLength={55}
                                autoComplete="new-password"
                                onChange={onFieldChange}
                                onKeyUp={onPasswordKeyUp}
                              />{' '}
                              <span
                                className={`show-pswd${!pwdVisible ? ' active' : ''}`}
                                onClick={showPassword}
                              >
                                Show
                              </span>
                              <span
                                className={`hide-pswd${pwdVisible ? ' active' : ''}`}
                                onClick={hidePassword}
                              >
                                Hide
                              </span>
                              <div className="password-conditions">
                                <span>Password conditions</span>
                                {/* NOTE: 'passowrd' typo is the legacy class name. */}
                                <ul className="passowrd-tooltip">
                                  <li>
                                    {"Password characters must be alphanumeric + !@#$%^&*()-_=+[]{}|;:'\",.<>?/\\\\"}
                                  </li>
                                  <li>Password must be 7 up to 55 characters.</li>
                                  <li>Password is case sensitive.</li>
                                </ul>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="contact-info col-xs-5">
                    <div className="col-xs-12">
                      <h4>Contact Information</h4>
                    </div>
                    <div className="popup-inputs-group contact-info-group row">
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label icon-telephone" />{' '}
                            <input
                              type="text"
                              autoComplete="off"
                              className="medium phone-number-input"
                              name="tel_office"
                              placeholder="Work"
                              defaultValue={user.tel_office ?? ''}
                              minLength={0}
                              maxLength={20}
                              pattern="[0-9\-,+,\(,\),., ]{0,20}"
                              onChange={onFieldChange}
                              onKeyPress={onPhoneKeyPress}
                            />
                            <span className="placeholder-label-work">Work</span>{' '}
                            <input
                              type="text"
                              autoComplete="off"
                              className="small phone-number-input"
                              name="tel_office_extension"
                              placeholder="Ext."
                              defaultValue={user.tel_office_extension ?? ''}
                              minLength={0}
                              maxLength={6}
                              pattern="[0-9\-,+,\(,\),., ]{0,6}"
                              onChange={onFieldChange}
                              onKeyPress={onPhoneKeyPress}
                            />
                            <span className="placeholder-label-ext">Ext.</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label icon-phone" />{' '}
                            <input
                              type="text"
                              autoComplete="off"
                              className="long phone-number-input"
                              name="tel_mobile"
                              placeholder="Mobile"
                              defaultValue={user.tel_mobile ?? ''}
                              pattern="[0-9\-,+,\(,\),., ]{0,20}"
                              onChange={onFieldChange}
                              onKeyPress={onPhoneKeyPress}
                            />
                            <span className="placeholder-label-mob">Mobile</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label icon-email" />{' '}
                            <input
                              type="email"
                              autoComplete="off"
                              className="long press_english"
                              name="email"
                              defaultValue={user.email ?? ''}
                              placeholder="Email"
                              onChange={onFieldChange}
                              onKeyPress={onEmailKeyPress}
                              onPaste={onEmailPaste}
                            />
                            <span className="placeholder-label-email">Email</span>
                          </div>
                        </div>
                      </div>
                      {/* The tmpl's second class attr ("top-margin access-type") is
                          dropped by the browser (duplicate attribute) — effective
                          class is col-xs-12 only. */}
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label col-xs-4">Access Type</span>
                            <span className="output col-xs-7">{accessType}</span>
                          </div>
                        </div>
                      </div>
                      {/* Hybrid AHU (KA-x) — read-only; the CMS Administrator sets
                          this from the Network user editor, not from here. */}
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label col-xs-4">User Skill</span>
                            <span className="output col-xs-7">
                              {(user.effective_user_skill ?? user.user_skill) === 'power'
                                ? 'Power User'
                                : 'Standard User'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12">
                            <span className="label col-xs-4">Role</span>
                            <span className="output col-xs-7">{user.role_name}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="note">
              <h6>
                <span>*</span>Field Required.
              </h6>
              <h6>
                <span>**</span>Field set by the Network Administrator.
              </h6>
            </div>
            <div className="popup-control-buttons col-xs-12">
              <div className="req-error">{reqError}</div>
              <button
                className={`btn done right${doneBlocked ? ' non-clickable' : ''}`}
                type="submit"
                name="done"
                value="done"
                onClick={onDone}
              >
                Done
              </button>
              <button className="btn right" type="button" name="cancel" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePopup;
